import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AuthShell, Field, inputCls, submitCls } from "@/components/AuthShell";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

export default function Login({ mode = "student" }) {
  const teacher = mode === "teacher";
  const admin = mode === "admin";
  const { t } = useLang();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ login: params.get("u") || "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { setUser } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      const wrong = teacher ? data.role !== "teacher" : admin ? data.role !== "admin" : data.role === "admin";
      if (wrong) {
        await api.post("/auth/logout").catch(() => {});
        setError(teacher ? t.auth.notTeacher : admin ? t.auth.notAdmin : t.auth.useAdmin);
        return;
      }
      setUser(data);
      toast.success(t.auth.welcome(data.name));
      nav(data.role === "admin" ? "/admin" : data.role === "teacher" ? "/teacher" : "/library");
    } catch (err) {
      const s = err?.response?.status;
      setError(s === 401 ? (admin ? t.auth.loginFailedAdmin : t.auth.loginFailed) : s === 429 ? t.auth.tooMany : s === 422 ? t.auth.invalid : errMsg(err) || t.auth.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={admin ? t.auth.adminTitle : teacher ? t.auth.teacherTitle : t.auth.loginTitle} subtitle={admin ? t.auth.adminSub : teacher ? t.auth.teacherSub : t.auth.loginSub}
      footer={admin ? null : teacher ? (
        <>{t.auth.studentQ} <Link to="/login" className="font-semibold text-[#8B3A2B] underline-offset-4 hover:underline" data-testid="teacher-to-student-login-link">{t.auth.studentGo}</Link></>
      ) : (
        <span className="flex flex-col gap-3">
          <span>{t.auth.noAccount} <Link to="/register" className="font-semibold text-[#8B3A2B] underline-offset-4 hover:underline" data-testid="login-to-register-link">{t.auth.createOne}</Link></span>
        </span>
      )}>
      <form onSubmit={submit} className="space-y-5" data-testid={admin ? "admin-login-form" : teacher ? "teacher-login-form" : "login-form"}>
        <Field label={admin ? t.auth.email : t.auth.usernameOrEmail}>
          <input type={admin ? "email" : "text"} required dir="ltr" autoCapitalize="off" autoCorrect="off" autoComplete="username" className={inputCls} value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} data-testid="login-email-input" />
        </Field>
        <Field label={t.auth.password}>
          <input type="password" required dir="ltr" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" />
        </Field>
        {error && <p className="text-sm text-red-800 bg-red-50 border border-red-200 p-3" data-testid="login-error">{error}</p>}
        {!admin && (
          <Link to="/forgot-password" className="block text-sm font-semibold text-[#8B3A2B] underline-offset-4 hover:underline" data-testid="login-forgot-link">{t.auth.forgot}</Link>
        )}
        <button disabled={loading} className={submitCls} data-testid="auth-login-submit">{loading ? t.auth.loggingIn : t.auth.loginBtn}</button>
      </form>
    </AuthShell>
  );
}
